import React, { useContext, useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Grid,
  IconButton,
  Tooltip,
  Fade
} from '@mui/material';
import { useAppNavigation } from '../hooks/useAppNavigation';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import TagIcon from '@mui/icons-material/Tag';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import HistoryIcon from '@mui/icons-material/History';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { AuthContext } from '../context/AuthContext';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import { placeholderImage } from '../utils/imageUtils';
import { directApi } from '../services/api';
import { getBackendBaseUrl } from '../utils/configUtils';
import offlineApi from '../services/offlineApi';
import BasicExport from '../components/BasicExport';
import OnboardingModal from '../components/OnboardingModal';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import letGoService from '../services/letGoService';

// Import styling from our design system
import { colors } from '../styles/styleConstants';

// Icons for empty state
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoIconButton, { areHintsEnabled } from '../components/InfoIconButton';


const Home = () => {
  const navigate = useAppNavigation();
  const { currentUser } = useContext(AuthContext);
  const [stats, setStats] = useState({ itemCount: 0, boxCount: 0 });
  const [loading, setLoading] = useState(true);
  const [recentItems, setRecentItems] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showHints] = useState(areHintsEnabled());
  const [weeklyScanDue, setWeeklyScanDue] = useState(false);
  const [highlightSimpleSwitch, setHighlightSimpleSwitch] = useState(false);

  // Highlight the "Simple" switch for the first ~3 unique active days
  // after the user switched from Simple → Full Dashboard, so they remember
  // they can switch back.
  useEffect(() => {
    const switchedAt = parseInt(
      localStorage.getItem('clutterlogix_switched_to_legacy_at') || '0',
      10
    );
    if (!switchedAt) return;

    const today = new Date().toISOString().slice(0, 10);
    let activeDays = [];
    try {
      const raw = localStorage.getItem('clutterlogix_legacy_active_days');
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) activeDays = parsed;
    } catch (e) {
      activeDays = [];
    }

    if (!activeDays.includes(today)) {
      activeDays.push(today);
      localStorage.setItem(
        'clutterlogix_legacy_active_days',
        JSON.stringify(activeDays)
      );
    }

    if (activeDays.length <= 3) {
      setHighlightSimpleSwitch(true);
    } else {
      localStorage.removeItem('clutterlogix_switched_to_legacy_at');
      localStorage.removeItem('clutterlogix_legacy_active_days');
    }
  }, []);

  // Check if user is new (no items and no boxes)
  const isNewUser = !loading && stats.itemCount === 0 && stats.boxCount === 0;
  // const [error, setError] = useState(null); // Error not displayed in UI
  
  // Fetch stats on component mount and listen for updates
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        console.log('Fetching home page stats...');
        
        // Determine which API to use based on offline mode
        const isOffline = !navigator.onLine;
        const offlineModeEnabled = await offlineApi.isOfflineModeEnabled();
        const apiToUse = (isOffline || offlineModeEnabled) ? offlineApi : directApi;
        console.log(`Using ${(isOffline || offlineModeEnabled) ? 'offline' : 'online'} API for stats`);
        
        // Get the actual items instead of just the count
        // Use high limit to get accurate count for Pro users
        let allItems = [];
        try {
          const itemsResponse = await apiToUse.get('/items?limit=10000');
          allItems = Array.isArray(itemsResponse.data) ? itemsResponse.data : [];
          console.log('Fetched items data for Home, total:', allItems.length);
        } catch (itemError) {
          console.error('Error fetching items for Home:', itemError);
          // Continue with empty items array
        }
        
        // Get the actual boxes instead of just the count
        let allBoxes = [];
        try {
          const boxesResponse = await apiToUse.get('/boxes');
          allBoxes = Array.isArray(boxesResponse.data) ? boxesResponse.data : [];
          console.log('Fetched boxes data for Home, total:', allBoxes.length);
        } catch (boxError) {
          console.error('Error fetching boxes for Home:', boxError);
          // Continue with empty boxes array
        }
        
        console.log(`Found ${allItems.length} items and ${allBoxes.length} boxes`);
        
        setStats({
          itemCount: allItems.length,
          boxCount: allBoxes.length
        });
        
      } catch (error) {
        console.error('Error fetching stats:', error);
        // setError('Failed to load statistics'); // Error not displayed in UI
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
    
    // Set up a listener for data refresh signals
    const checkForDataRefresh = () => {
      // Check for specific item deletion signals
      if (localStorage.getItem('cubxly_items_deleted')) {
        console.log('Items deleted, refreshing Home page stats...');
        fetchStats();
        return;
      }
      
      // Check for general data refresh signal
      if (localStorage.getItem('cubxly_data_refresh')) {
        console.log('Data refresh detected, updating Home page stats...');
        fetchStats();
      }
    };
    
    // Set up event listeners
    window.addEventListener('storage', checkForDataRefresh);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkForDataRefresh();
      }
    });
    
    // Check for refresh signals on mount
    checkForDataRefresh();
    
    // Clean up event listeners on unmount
    return () => {
      window.removeEventListener('storage', checkForDataRefresh);
      document.removeEventListener('visibilitychange', () => {});
    };
  }, []);
  
  // Fetch recent items
  useEffect(() => {
    const fetchRecentItems = async () => {
      try {
        setRecentLoading(true);
        const isOffline = !navigator.onLine;
        const offlineModeEnabled = await offlineApi.isOfflineModeEnabled();
        const apiToUse = (isOffline || offlineModeEnabled) ? offlineApi : directApi;
        
        const response = await apiToUse.get('/items/recent?limit=5');
        const items = Array.isArray(response.data) ? response.data : [];
        console.log('Fetched recent items:', items.length);
        setRecentItems(items);
      } catch (error) {
        console.error('Error fetching recent items:', error);
        setRecentItems([]);
      } finally {
        setRecentLoading(false);
      }
    };
    
    fetchRecentItems();
  }, []);
  
  // Check if weekly declutter scan is due
  useEffect(() => {
    const checkWeeklyScan = async () => {
      try {
        const result = await letGoService.checkWeeklyScanDue();
        if (result.scan_due) {
          setWeeklyScanDue(true);
        }
      } catch (error) {
        console.error('Error checking weekly scan:', error);
      }
    };
    
    checkWeeklyScan();
  }, []);
  
  // Helper function to get user display name from various possible fields
  const getUserDisplayName = (user) => {
    if (!user) return 'User';
    
    // Try different possible name fields
    return user.name || user.full_name || user.email?.split('@')[0] || 'User';
  };
  
  // Navigation handlers
  const handleAddClick = () => navigate.to.ADD();
  const handleFindClick = () => navigate.to.FIND();
  const handleLocationsClick = () => navigate.to.LOCATIONS();
  
  // AI Scan handler - navigate to Let Go page with trigger flag
  const handleAIScan = () => {
    // Navigate to Let Go page with a query param to trigger scan
    navigate('let-go?scan=true');
  };

  return (
    <Layout 
      title="ClutterLogix" 
      disableBackButton={true}
      customTitleComponent={(
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img 
            src="/logo192.png" 
            alt="ClutterLogix" 
            style={{ 
              height: '32px', 
              width: '32px', 
              marginRight: '12px',
              filter: 'brightness(0) invert(1)', // Make logo white on dark background
            }} 
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>ClutterLogix</Typography>
        </Box>
      )}
    >
      {/* Weekly Declutter Scan Notification */}
      {weeklyScanDue && (
        <Box sx={{
          backgroundColor: '#9c27b0',
          padding: 2,
          marginTop: -3,
          marginLeft: -3,
          marginRight: -3,
          marginBottom: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
            🧹 Time for your weekly declutter! Find items to let go.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              sx={{ 
                backgroundColor: 'white', 
                color: '#9c27b0',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
              }}
              onClick={() => {
                setWeeklyScanDue(false);
                navigate('let-go?scan=true');
              }}
            >
              Start Scan
            </Button>
            <Button
              size="small"
              variant="text"
              sx={{ color: 'white' }}
              onClick={() => setWeeklyScanDue(false)}
            >
              Dismiss
            </Button>
          </Box>
        </Box>
      )}
      
      <Box sx={{
        backgroundColor: colors.slateBlue || colors.secondary,
        padding: 3,
        paddingBottom: 4, // Extra bottom padding for stats section
        marginTop: weeklyScanDue ? 0 : -3, // To offset the padding from Layout
        marginLeft: -3,
        marginRight: -3,
        marginBottom: 3,
        borderRadius: '8px',
        color: colors.lightGray
      }}>
        {/* Welcome Header */}
        {currentUser && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <Avatar
                user={currentUser}
                size={48}
                showRefresh={false}
                sx={{
                  mr: 2,
                  flexShrink: 0,
                  border: `2px solid ${colors.softWhite}`,
                }}
              />
              <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                <Typography variant="h5" noWrap sx={{
                  fontWeight: 600,
                  color: colors.lightGray,
                  fontFamily: "'Poppins', sans-serif"
                }}>
                  Welcome, {getUserDisplayName(currentUser)}
                </Typography>
                <Typography variant="body2" noWrap sx={{ color: 'rgba(226, 232, 240, 0.8)' }}>
                  Let's manage your inventory
                </Typography>
              </Box>
            </Box>
            
            {/* Quick switch to simple view */}
            <Tooltip
              title={highlightSimpleSwitch ? 'You can switch back to the Simple view anytime' : ''}
              placement="bottom"
              arrow
              disableHoverListener={!highlightSimpleSwitch}
              disableFocusListener={!highlightSimpleSwitch}
              disableTouchListener={!highlightSimpleSwitch}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<ViewCompactIcon />}
                onClick={() => {
                  // Snooze the Simple-home nudge so the user isn't immediately
                  // pushed back to Full after returning to Simple.
                  localStorage.setItem(
                    'clutterlogix_simple_nudge_snoozed_until',
                    String(Date.now() + 7 * 24 * 60 * 60 * 1000)
                  );
                  localStorage.setItem('clutterlogix_home_mode', 'simple');
                  window.location.reload();
                }}
                sx={{
                  borderColor: highlightSimpleSwitch ? colors.softWhite : 'rgba(255,255,255,0.3)',
                  color: colors.softWhite,
                  backgroundColor: highlightSimpleSwitch ? 'rgba(255,255,255,0.12)' : 'transparent',
                  fontSize: '0.875rem',
                  fontWeight: highlightSimpleSwitch ? 600 : 400,
                  textTransform: 'none',
                  py: 0.5,
                  px: 1,
                  minWidth: 'auto',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  animation: highlightSimpleSwitch
                    ? 'legacy-home-simple-pulse 2.2s ease-in-out infinite'
                    : 'none',
                  '@keyframes legacy-home-simple-pulse': {
                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.45)' },
                    '50%': { boxShadow: '0 0 0 8px rgba(255, 255, 255, 0)' }
                  },
                  '&:hover': {
                    borderColor: colors.softWhite,
                    backgroundColor: 'rgba(255,255,255,0.18)'
                  }
                }}
              >
                Simple
              </Button>
            </Tooltip>
            
            {/* <Tooltip title="Offline Mode" placement="left" TransitionComponent={Fade} arrow>
              <IconButton
                sx={{ 
                  color: !navigator.onLine ? colors.softWhite : 'rgba(226, 232, 240, 0.5)',
                  backgroundColor: !navigator.onLine ? 'rgba(255, 152, 0, 0.2)' : 'transparent',
                }}
                onClick={() => navigator.onLine ? null : navigate.to.OFFLINE_ITEMS()}
              >
                <WifiOffIcon />
              </IconButton>
            </Tooltip> */}
          </Box>
        )}
        
        {/* Action Buttons in Dark Header */}
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<AddIcon />}
                onClick={handleAddClick}
                sx={{ 
                  py: 1, 
                  backgroundColor: colors.actionBlue,
                  color: colors.softWhite,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: colors.actionBlue,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 10px rgba(0,0,0,0.2)'
                  }
                }}
              >
                Add Items/Box
              </Button>
              {showHints && (
                <Box sx={{ position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)' }}>
                  <InfoIconButton hint="Add new items or create a box to organize your belongings" color="light" />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={handleFindClick}
                sx={{ 
                  py: 1, 
                  borderColor: 'rgba(226, 232, 240, 0.5)',
                  color: colors.lightGray,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: colors.softWhite,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                Find Items
              </Button>
              {showHints && (
                <Box sx={{ position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)' }}>
                  <InfoIconButton hint="Search your inventory using AI - try natural language like 'red tools'" color="light" />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<LocationOnIcon />}
                onClick={handleLocationsClick}
                sx={{ 
                  py: 1, 
                  borderColor: 'rgba(226, 232, 240, 0.5)',
                  color: colors.lightGray,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: colors.softWhite,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                Locations
              </Button>
              {showHints && (
                <Box sx={{ position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)' }}>
                  <InfoIconButton hint="Browse items organized by storage location (garage, basement, etc.)" color="light" />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<QrCodeScannerIcon />}
                onClick={() => navigate.to.ASSIGN_QR()}
                sx={{ 
                  py: 1, 
                  borderColor: 'rgba(226, 232, 240, 0.5)',
                  color: colors.lightGray,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: colors.softWhite,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                QR Labels
              </Button>
              {showHints && (
                <Box sx={{ position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)' }}>
                  <InfoIconButton hint="Print QR labels for boxes - scan to quickly view contents" color="light" />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<VolunteerActivismIcon />}
                onClick={() => navigate.to.LET_GO()}
                sx={{ 
                  py: 1, 
                  borderColor: 'rgba(226, 232, 240, 0.5)',
                  color: colors.lightGray,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: colors.softWhite,
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                Let Go
              </Button>
              {showHints && (
                <Box sx={{ position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)' }}>
                  <InfoIconButton hint="Review items you haven't used in a while and decide what to donate" color="light" />
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<AutoFixHighIcon />}
                onClick={handleAIScan}
                sx={{ 
                  py: 1, 
                  backgroundColor: '#9c27b0',
                  color: colors.softWhite,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: '#7b1fa2',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 10px rgba(0,0,0,0.2)'
                  }
                }}
              >
                Declutter
              </Button>
              {showHints && (
                <Box sx={{ position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)' }}>
                  <InfoIconButton hint="AI analyzes your inventory to find items you might want to donate or let go" color="light" />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
        
        {/* Dashboard Stats - Now part of the same unified box */}
        <Box sx={{ 
          mt: 4, // Margin top to separate from buttons
          color: colors.lightGray
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.softWhite }}>
              Inventory Summary
            </Typography>
            {loading && (
              <Typography variant="caption" sx={{ color: 'rgba(226, 232, 240, 0.7)' }}>
                Refreshing...
              </Typography>
            )}
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  padding: 1.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    transform: 'translateY(-2px)',
                  }
                }}
                onClick={() => navigate.to.ALL_ITEMS()}
              >
                <CategoryIcon sx={{ fontSize: 28, mb: 0.5, color: colors.softWhite }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.softWhite }}>
                  {stats.itemCount}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(226, 232, 240, 0.7)' }}>
                  Items
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  padding: 1.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    transform: 'translateY(-2px)',
                  }
                }}
                onClick={() => navigate.to.ALL_BOXES()}
              >
                <InventoryIcon sx={{ fontSize: 28, mb: 0.5, color: colors.softWhite }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.softWhite }}>
                  {stats.boxCount}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(226, 232, 240, 0.7)' }}>
                  Boxes
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  padding: 1.5,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    transform: 'translateY(-2px)',
                  }
                }}
                onClick={() => navigate.to.TAGS()}
              >
                <TagIcon sx={{ fontSize: 28, mb: 0.5, color: colors.softWhite }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.softWhite }}>
                  {stats.itemCount > 0 ? Math.round(stats.itemCount * 0.8) : 0}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(226, 232, 240, 0.7)' }}>
                  Tags
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      
      {/* Recently Added Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: colors.slateBlue,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <HistoryIcon fontSize="small" />
            Recent Activity
          </Typography>
          
          <Button 
            variant="text" 
            endIcon={<MoreHorizIcon />}
            size="small"
            onClick={() => navigate.to.ALL_ITEMS()}
            sx={{ color: colors.actionBlue }}
          >
            View All
          </Button>
        </Box>
        
        <Box sx={{
          backgroundColor: colors.softWhite,
          borderRadius: 3,
          padding: 0,
          overflow: 'hidden'
        }}>
          {recentLoading ? (
            <Typography sx={{ p: 3, textAlign: 'center', color: colors.mediumSlateBlue }}>
              Loading recent items...
            </Typography>
          ) : recentItems.length > 0 ? (
            <Box>
              {recentItems.map((item, index) => (
                <Box
                  key={item.id}
                  onClick={() => navigate.to.ITEM_DETAIL(item.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 2,
                    cursor: 'pointer',
                    borderBottom: index < recentItems.length - 1 ? `1px solid ${colors.lightGray}` : 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={item.image_url ? `${getBackendBaseUrl()}/api/v2/files/item/${item.id}/${item.user_id || 3}` : placeholderImage}
                    alt={item.name}
                    onError={(e) => { e.target.src = placeholderImage; }}
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      objectFit: 'cover',
                      mr: 2,
                      backgroundColor: colors.lightGray,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 500, 
                        color: colors.slateBlue,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.name || 'Unnamed Item'}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: colors.mediumSlateBlue,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.description || 'No description'}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.mediumSlateBlue, ml: 1 }}>
                    {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            /* Enhanced empty state for new users */
            <Box sx={{ p: 4, pb: { xs: 10, sm: 4 }, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: `${colors.actionBlue}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <RocketLaunchIcon sx={{ fontSize: 40, color: colors.actionBlue }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.slateBlue, mb: 1 }}>
                Ready to Get Organized?
              </Typography>
              <Typography variant="body2" sx={{ color: colors.mediumSlateBlue, mb: 3, maxWidth: 280, mx: 'auto' }}>
                Start by creating a box, then add items with photos. Our AI will automatically describe and tag everything!
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate.to.CREATE_BOX()}
                sx={{
                  backgroundColor: colors.actionBlue,
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  zIndex: 1001,
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: colors.actionBlue,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }
                }}
              >
                Create Your First Box
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => setShowOnboarding(true)}
                sx={{
                  mt: 2,
                  color: colors.mediumSlateBlue,
                  textTransform: 'none',
                  display: 'block',
                  mx: 'auto',
                  zIndex: 1001,
                  position: 'relative'
                }}
              >
                Show me how it works
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Export Section - Available to all users including Free */}
      {stats.itemCount > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: colors.slateBlue,
            mb: 2
          }}>
            Export Your Data
          </Typography>
          
          <Box sx={{
            backgroundColor: colors.softWhite,
            borderRadius: 3,
            padding: 3,
            textAlign: 'center'
          }}>
            <Typography variant="body2" sx={{ 
              color: colors.mediumSlateBlue, 
              mb: 2 
            }}>
              Export your inventory data as CSV files for backup or external use.
            </Typography>
            <BasicExport />
          </Box>
        </Box>
      )}

      {/* Onboarding Modal for first-time users */}
      <OnboardingModal 
        forceShow={showOnboarding} 
        onComplete={() => setShowOnboarding(false)} 
      />
    </Layout>
  );
};

export default Home;
